import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Lock, Upload, ArrowLeft, X, FileImage, FileVideo } from "lucide-react";

const MAX_IMG_MB = { free: 12, vip: 20, creator: 30 };
const MAX_VID_MB = { free: 100, vip: 250, creator: 500 };

export default function LeakUpload() {
  const { user } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [albumId, setAlbumId] = useState(null);
  const [consent, setConsent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null); // {current,total,name}
  const [error, setError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]); // Array<File>
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    // Hent eller opprett det private leak-albumet
    api.get("/leak-check/upload-album")
      .then((r) => setAlbumId(r.data?.album?.id))
      .catch((e) => setError(formatApiError(e)));
  }, []);

  const userTier = user?.is_creator ? "creator" : (user?.tier || "free");
  const imgLimit = MAX_IMG_MB[userTier];
  const vidLimit = MAX_VID_MB[userTier];

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    // Filtrer ut duplikater basert på name+size+lastModified
    setSelectedFiles((prev) => {
      const key = (f) => `${f.name}|${f.size}|${f.lastModified}`;
      const existing = new Set(prev.map(key));
      const next = [...prev];
      for (const f of arr) {
        if (!existing.has(key(f))) next.push(f);
      }
      return next;
    });
    setError("");
  };

  const onFilesChange = () => {
    addFiles(fileRef.current?.files);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeFile = (idx) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploading) return;
    addFiles(e.dataTransfer?.files);
  };

  const upload = async () => {
    setError("");
    if (!albumId) return setError("Leak-album ikke tilgjengelig.");
    if (!consent) return setError("Du må bekrefte samtykke før opplasting.");
    const files = selectedFiles;
    if (files.length === 0) return setError("Velg minst én fil.");

    // Valider størrelser
    const bad = [];
    for (const f of files) {
      const isImg = f.type.startsWith("image/");
      const isVid = f.type.startsWith("video/");
      if (!isImg && !isVid) { bad.push(`${f.name}: ikke bilde/video`); continue; }
      const limit = isVid ? vidLimit : imgLimit;
      if (f.size > limit * 1024 * 1024) {
        bad.push(`${f.name}: ${(f.size / 1024 / 1024).toFixed(1)} MB overstiger ${limit} MB`);
      }
    }
    if (bad.length) return setError("Kan ikke laste opp:\n• " + bad.join("\n• "));

    setUploading(true);
    const errors = [];
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProgress({ current: i + 1, total: files.length, name: file.name });
      const isVideo = file.type.startsWith("video/");
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("consent", "true");
        fd.append("caption", "");
        fd.append("visibility", "private");
        fd.append("requires_tier", "free");
        fd.append("price_tokens", "0");
        fd.append("access_friends_free", "false");
        fd.append("access_vip_free", "false");
        fd.append("watermark", "off");
        const endpoint = isVideo ? `/albums/${albumId}/videos` : `/albums/${albumId}/images`;
        await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) {
        errors.push(`${file.name}: ${formatApiError(e)}`);
      }
    }
    setProgress(null);
    setUploading(false);

    // Auto-marker som leak-referanser
    try { await api.patch("/leak-check/references/auto-mark"); } catch { /* ignore */ }

    if (errors.length) {
      setError(`Lastet opp ${ok} av ${files.length}. Feilet:\n• ${errors.join("\n• ")}`);
      return;
    }
    toast.success(`${ok} referansefil${ok === 1 ? "" : "er"} lagt til`);
    nav("/leak-sjekk");
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <button
          type="button"
          onClick={() => nav("/leak-sjekk")}
          className="font-meta text-xs tracking-[0.15em] text-[var(--cream-soft)] hover:text-[var(--cream)] inline-flex items-center gap-1.5 mb-6"
          data-testid="leak-upload-back-btn"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Tilbake til leak-sjekk
        </button>

        <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-2" data-testid="leak-upload-title">
          Legg til referansefiler
        </h1>
        <p className="font-manrope text-sm text-[var(--cream-soft)] mb-6">
          Last opp bilder/videoer du vil overvåke mot lekkasjer på nettet.
        </p>

        {/* PRIVAT-BANNER */}
        <div
          className="border border-[var(--magenta)] bg-[var(--magenta)]/10 p-4 mb-8 flex items-start gap-3"
          data-testid="leak-upload-private-banner"
        >
          <Lock className="h-5 w-5 text-[var(--magenta)] shrink-0 mt-0.5" />
          <div className="font-manrope text-sm text-[var(--cream)]">
            <div className="font-meta text-xs tracking-[0.15em] text-[var(--magenta)] uppercase mb-1">
              Låst privat
            </div>
            Kun du kan se disse filene.
          </div>
        </div>

        <div className="editorial-card p-6 space-y-5">
          {/* Drop-zone + fil-velger */}
          <div>
            <div className="font-meta mb-2">Velg filer</div>
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => !uploading && fileRef.current?.click()}
              role="button"
              tabIndex={0}
              className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? "border-[var(--magenta)] bg-[var(--magenta)]/10"
                  : "border-white/20 hover:border-[var(--magenta)]/60 bg-black/20"
              } ${uploading ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid="leak-upload-dropzone"
            >
              <Upload className="h-8 w-8 mx-auto mb-3 text-[var(--cream-soft)]" />
              <div className="font-manrope text-sm text-[var(--cream)] mb-1">
                {dragOver ? "Slipp filene her" : "Dra og slipp filer her"}
              </div>
              <div className="font-manrope text-xs text-[var(--cream-soft)]">
                eller <span className="text-[var(--magenta)] underline underline-offset-2">klikk for å velge</span>
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*,video/mp4,video/webm,video/quicktime,video/x-matroska"
              onChange={onFilesChange}
              disabled={uploading}
              className="hidden"
              data-testid="leak-upload-file-input"
            />
            <div className="font-meta mt-2 text-[10px]">
              Bilde maks {imgLimit} MB · Video maks {vidLimit} MB
            </div>
          </div>

          {/* Kø: valgte filer */}
          {selectedFiles.length > 0 && (
            <div className="border-t border-white/10 pt-4">
              <div
                className="font-meta text-xs text-[var(--cream)] mb-2"
                data-testid="leak-upload-file-count"
              >
                {selectedFiles.length} fil{selectedFiles.length === 1 ? "" : "er"} valgt
              </div>
              <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="leak-upload-file-list">
                {selectedFiles.map((f, i) => {
                  const isVid = f.type.startsWith("video/");
                  const Icon = isVid ? FileVideo : FileImage;
                  return (
                    <li
                      key={`${f.name}-${f.size}-${i}`}
                      className="flex items-center gap-2 px-2 py-1.5 bg-black/40 border border-white/5 text-xs font-manrope"
                    >
                      <Icon className="h-3.5 w-3.5 text-[var(--cream-soft)] shrink-0" />
                      <span className="flex-1 truncate text-[var(--cream)]" title={f.name}>{f.name}</span>
                      <span className="text-[var(--cream-faint)] shrink-0">
                        {(f.size / 1024 / 1024).toFixed(1)} MB
                      </span>
                      {!uploading && (
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="text-[var(--cream-soft)] hover:text-[var(--magenta)] shrink-0"
                          data-testid={`leak-upload-remove-file-${i}`}
                          title="Fjern fra listen"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
              {!uploading && (
                <button
                  type="button"
                  onClick={() => setSelectedFiles([])}
                  className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] hover:text-[var(--magenta)] mt-2"
                  data-testid="leak-upload-clear-all"
                >
                  Fjern alle
                </button>
              )}
            </div>
          )}

          <label className="flex items-start gap-3 cursor-pointer text-sm text-[var(--cream)] font-manrope border-t border-white/10 pt-4">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              disabled={uploading}
              className="mt-0.5 h-4 w-4 accent-[var(--magenta)]"
              data-testid="leak-upload-consent"
            />
            <span>
              Jeg bekrefter at alle på bildet/videoen er over 18 år og at jeg har rettighetene
              til innholdet.
            </span>
          </label>

          {progress && (
            <div className="border border-white/10 p-3 bg-black/40" data-testid="leak-upload-progress">
              <div className="font-meta text-xs text-[var(--cream)] mb-2">
                Laster opp {progress.current} av {progress.total}: <span className="text-[var(--cream-soft)] normal-case tracking-normal">{progress.name}</span>
              </div>
              <div className="h-1.5 bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-[var(--magenta)] transition-all"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {error && (
            <div
              className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 whitespace-pre-wrap"
              data-testid="leak-upload-error"
            >
              {error}
            </div>
          )}

          <button
            onClick={upload}
            disabled={uploading || !albumId}
            className="btn-magenta"
            data-testid="leak-upload-submit"
          >
            <Upload className="h-3.5 w-3.5 inline mr-2" />
            {uploading ? "Laster opp…" : "Last opp"}
          </button>
        </div>
      </div>
    </Layout>
  );
}
